import { z } from 'zod';
import { PASSWORD_REQUIREMENTS } from './utils';
import { WEBHOOK_EVENTS } from './constants';
// ============================================
// PASSWORD SCHEMA (Shared between frontend and backend)
// ============================================
/**
 * Shared password validation schema using PASSWORD_REQUIREMENTS
 * This ensures consistency between frontend validation and backend validation
 */
export const passwordSchema = z
    .string()
    .min(PASSWORD_REQUIREMENTS.minLength, `Password must be at least ${PASSWORD_REQUIREMENTS.minLength} characters`)
    .max(100, 'Password must be less than 100 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number');
// ============================================
// AUTH SCHEMAS
// ============================================
export const loginSchema = z.object({
    email: z.string().email('Invalid email address').max(255),
    password: z.string().min(PASSWORD_REQUIREMENTS.minLength, `Password must be at least ${PASSWORD_REQUIREMENTS.minLength} characters`).max(100, 'Password must be less than 100 characters'),
});
export const magicLinkRequestSchema = z.object({
    email: z.string().email('Invalid email address').max(255),
});
export const magicLinkVerifySchema = z.object({
    token: z.string().min(1, 'Token is required').max(200),
});
export const refreshTokenSchema = z.object({
    refreshToken: z.string().min(1, 'Refresh token is required').max(2000),
});
// ============================================
// FEEDBACK SCHEMAS
// ============================================
// Base feedback schema with validation matching the API
// API may add additional fields (imageKey, etc.) but these are the core fields
export const feedbackSchema = z.object({
    qrCode: z.string().min(1, 'QR code is required').max(25, 'QR code is too long'),
    rating: z.number().int().min(1, 'Rating must be at least 1').max(5, 'Rating must be at most 5').optional(),
    comment: z.string().max(500, 'Comment must be 500 characters or less').optional(),
});
// ============================================
// TASK SCHEMAS
// ============================================
export const taskStatusUpdateSchema = z.object({
    status: z.enum(['NEW', 'IN_PROGRESS', 'COMPLETED']),
    note: z.string().max(500, 'Note must be 500 characters or less').optional(),
});
export const taskQuerySchema = z.object({
    status: z.enum(['NEW', 'IN_PROGRESS', 'COMPLETED']).optional(),
    assignedUserId: z.string().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
// ============================================
// ADMIN SCHEMAS - Buildings
// ============================================
export const createBuildingSchema = z.object({
    name: z.string().min(1, 'Name is required').max(255),
    address: z.string().max(500).optional(),
});
export const updateBuildingSchema = createBuildingSchema.partial();
// ============================================
// ADMIN SCHEMAS - Locations (formerly Floors)
// ============================================
export const createLocationSchema = z.object({
    name: z.string().min(1, 'Name is required').max(255),
    buildingId: z.string().min(1, 'Building is required'),
    parentId: z.string().optional(),
    locationType: z.string().max(100).default('floor'),
    locationTypeId: z.string().optional(),
    description: z.string().max(1000).optional(),
    level: z.number().int().optional(),
    icon: z.string().max(100).optional(),
});
export const updateLocationSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    locationType: z.string().max(100).optional(),
    locationTypeId: z.string().optional(),
    description: z.string().max(1000).optional(),
    level: z.number().int().optional(),
    icon: z.string().max(100).optional(),
});
// ============================================
// ADMIN SCHEMAS - Amenities
// ============================================
export const createAmenitySchema = z.object({
    name: z.string().min(1, 'Name is required').max(255),
    description: z.string().max(1000).optional(),
    locationId: z.string().min(1, 'Location is required'),
    amenityTypeId: z.string().min(1, 'Amenity type is required'),
});
export const updateAmenitySchema = createAmenitySchema.partial();
// ============================================
// ADMIN SCHEMAS - Amenity Types
// ============================================
export const createAmenityTypeSchema = z.object({
    name: z.string().min(1, 'Name is required').max(255),
    icon: z.string().max(100).optional(),
    defaultSlaMinutes: z.number().int().min(1).optional(),
});
export const updateAmenityTypeSchema = createAmenityTypeSchema.partial();
// ============================================
// ADMIN SCHEMAS - Users
// ============================================
export const createUserSchema = z.object({
    email: z.string().email('Invalid email address'),
    name: z.string().max(255).optional(),
    password: passwordSchema,
    roles: z.array(z.enum(['ADMIN', 'MANAGER', 'TECHNICIAN'])).min(1).max(3),
    isActive: z.boolean().default(true),
});
export const updateUserSchema = z.object({
    email: z.string().email('Invalid email address').optional(),
    name: z.string().max(255).optional(),
    password: passwordSchema.optional(),
    roles: z.array(z.enum(['ADMIN', 'MANAGER', 'TECHNICIAN'])).min(1).max(3).optional(),
    isActive: z.boolean().optional(),
});
// ============================================
// ADMIN SCHEMAS - Routing Rules
// ============================================
export const createRoutingRuleSchema = z.object({
    name: z.string().min(1, 'Name is required').max(255),
    amenityTypeId: z.string().optional(),
    buildingId: z.string().optional(),
    locationId: z.string().optional(),
    assignedUserId: z.string().optional(),
    priority: z.number().int().min(0).default(100),
    isActive: z.boolean().default(true),
});
export const updateRoutingRuleSchema = createRoutingRuleSchema.partial();
// ============================================
// ADMIN SCHEMAS - QR Codes
// ============================================
export const generateQrCodeSchema = z.object({
    amenityId: z.string().min(1, 'Amenity is required'),
});
export const bulkGenerateQrCodeSchema = z.object({
    amenityIds: z.array(z.string().min(1)).min(1).max(100),
});
// ============================================
// QUERY SCHEMAS
// ============================================
export const paginationSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export const feedbackQuerySchema = z.object({
    buildingId: z.string().optional(),
    locationId: z.string().optional(),
    amenityTypeId: z.string().optional(),
    amenityId: z.string().optional(),
    dateFrom: z.coerce.date().optional(),
    dateTo: z.coerce.date().optional(),
    hasIssues: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export const amenityPerformanceQuerySchema = z.object({
    buildingId: z.string().optional(),
    dateFrom: z.coerce.date().optional(),
    dateTo: z.coerce.date().optional(),
});
export const exportFeedbackQuerySchema = z.object({
    buildingId: z.string().optional(),
    locationId: z.string().optional(),
    amenityTypeId: z.string().optional(),
    amenityId: z.string().optional(),
    dateFrom: z.coerce.date().optional(),
    dateTo: z.coerce.date().optional(),
    hasIssues: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
});
export const exportTasksQuerySchema = z.object({
    status: z.enum(['NEW', 'IN_PROGRESS', 'COMPLETED']).optional(),
    assignedUserId: z.string().optional(),
    dateFrom: z.coerce.date().optional(),
    dateTo: z.coerce.date().optional(),
});
// ============================================
// WEBHOOK SCHEMAS
// ============================================
export const createWebhookSchema = z.object({
    name: z.string().min(1, 'Name is required').max(255),
    url: z.string().url('Invalid URL').max(2048),
    events: z.array(z.enum(WEBHOOK_EVENTS)).min(1, 'At least one event is required'),
    isActive: z.boolean().optional().default(true),
});
export const updateWebhookSchema = createWebhookSchema.partial();
//# sourceMappingURL=schemas.js.map